import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import api, { API } from "@/lib/api";
import { fileHref } from "@/components/CustomField";
import { useAuth } from "@/context/AuthContext";
import { STATUS_META, isLimitedMemberView, isNationalLevel } from "@/lib/constants";

const SHOWN = new Set(["title", "first_name", "middle_name", "last_name", "sex", "date_of_birth", "age", "ssnit_number", "contact_number",
  "alternate_contact_number", "email", "next_of_kin_name", "next_of_kin_contact", "next_of_kin_relationship", "facility_name", "facility_type",
  "district", "region_name", "professional_grade", "rank", "ghana_card_number", "marital_status", "spouse_name", "number_of_children",
  "biological_children_names", "children", "no_children", "mother_name", "mother_status", "father_name", "father_status", "emergency_contact_name",
  "emergency_contact_phone", "emergency_contact_email", "emergency_contact_address", "worked_at_previous_facility", "previous_facility_name",
  "previous_facility_location", "previous_facility_type", "education", "rejection_reason", "staff_id", "membership_id", "member_type",
  "application_status", "town", "pays_dues", "tshirt_size", "submitted_at", "approved_at", "profile_picture", "full_name", "custom_fields"]);
const HIDDEN = /(^id$|^needs_|_sent$|^member_type_|_id$|^role$|password|token|^completion$|^retiring$|^archived|^updated_at$|^created_at$|^must_|^email_verif|^last_login|^source$|^v$|_hash$|^assigned_|_by$)/;
const human = (k) => k.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());
const asText = (v) => (Array.isArray(v) ? v.map((x) => (typeof x === "object" ? Object.values(x).filter(Boolean).join(" · ") : x)).join("; ")
  : typeof v === "object" ? Object.entries(v).map(([a, b]) => `${human(a)}: ${b}`).join(", ") : String(v));
const fmtDate = (v) => (v ? new Date(v).toLocaleString() : "");

function CustomAnswers({ answers }) {
  const [labels, setLabels] = useState({});
  useEffect(() => { api.get("/forms/labels").then(({ data }) => setLabels(data)).catch(() => {}); }, []);
  const rows = Object.entries(answers || {}).filter(([, v]) => v !== "" && !(Array.isArray(v) && !v.length));
  if (!rows.length) return null;
  return (
    <Section title="Additional Information">
      {rows.map(([k, v]) => labels[k]?.type === "file"
        ? <div key={k} data-testid={`member-custom-${k}`}><p className="text-[11px] uppercase tracking-wider text-muted-foreground">{labels[k].label}</p><a className="text-sm text-primary underline" href={fileHref(v)} target="_blank" rel="noreferrer">View file</a></div>
        : <Field key={k} label={labels[k]?.label || "Additional answer"} value={Array.isArray(v) ? v.join(", ") : v === "yes" ? "Yes" : v === "no" ? "No" : v} testId={`member-custom-${k}`} />)}
    </Section>
  );
}

function NationalExtras({ member }) {
  const other = Object.entries(member).filter(([k, v]) => !SHOWN.has(k) && !HIDDEN.test(k) && v !== null && v !== "" && v !== false && !(Array.isArray(v) && !v.length));
  return (
    <>
      <Section title="Registration Record">
        <Field label="Full Name" value={[member.title, member.first_name, member.middle_name, member.last_name].filter(Boolean).join(" ")} testId="member-full-registration-name" />
        <Field label="Staff ID" value={member.staff_id} />
        <Field label="Membership Number" value={member.membership_id || "Not assigned"} />
        <Field label="Member Type" value={human(member.member_type || "public")} />
        <Field label="Application Status" value={(STATUS_META[member.application_status] || {}).label || member.application_status} />
        <Field label="Town" value={member.town} />
        <Field label="Dues Payment" value={member.pays_dues} />
        <Field label="T-shirt Size" value={member.tshirt_size} />
        <Field label="Submitted" value={fmtDate(member.submitted_at)} />
        <Field label="Approved" value={fmtDate(member.approved_at)} />
        <Field label="Previous Employment" value={member.worked_at_previous_facility ? human(member.worked_at_previous_facility) : ""} />
      </Section>
      {member.children?.length > 0 && (
        <div className="mt-4" data-testid="member-children-list">
          <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Biological Children</p>
          <div className="grid gap-2 sm:grid-cols-2">
            {member.children.map((c, i) => (
              <div key={i} className="rounded-md border border-border p-3 text-sm"><p className="font-medium">{c.full_name || "—"}</p>
                <p className="text-xs text-muted-foreground">{c.sex || "—"} · {c.date_of_birth || "—"}</p></div>
            ))}
          </div>
        </div>
      )}
      {other.length > 0 && (
        <Section title="Other Registration Details">
          {other.map(([k, v]) => <Field key={k} label={human(k)} value={asText(v)} testId={`member-extra-${k}`} />)}
        </Section>
      )}
      <CustomAnswers answers={member.custom_fields} />
    </>
  );
}

const photoUrl = (p) =>
  p ? (p.startsWith("data:") || p.startsWith("http") ? p : `${API}/files/${p}?auth=${localStorage.getItem("token")}`) : "";

function Avatar({ member, size = 64 }) {
  const names = (member.full_name || `${member.first_name || ""} ${member.last_name || ""}`).trim().split(/\s+/);
  const initials = `${names[0]?.[0] || ""}${names.length > 1 ? names[names.length - 1]?.[0] || "" : ""}`.toUpperCase();
  const src = photoUrl(member.profile_picture);
  return (
    <div data-testid="member-details-photo"
      className="rounded-full bg-muted overflow-hidden border border-border flex items-center justify-center shrink-0 text-lg font-semibold text-muted-foreground"
      style={{ width: size, height: size }}
    >
      {src ? <img src={src} alt={member.full_name || "Member photo"} className="h-full w-full object-cover" /> : (initials || "?")}
    </div>
  );
}

function Field({ label, value, testId }) {
  return (
    <div data-testid={testId}>
      <p className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</p>
      <p className="text-sm font-medium mt-0.5 break-words">{value || "—"}</p>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="mt-4">
      <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">{title}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">{children}</div>
    </div>
  );
}

function LimitedMemberDetails({ member, open, onOpenChange, loading, footerSlot }) {
  const status = STATUS_META[member.application_status] || STATUS_META.incomplete;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="member-details-dialog">
        <DialogHeader>
          <div className="flex items-center gap-4 text-left">
            <Avatar member={member} />
            <div className="min-w-0">
              <DialogTitle className="font-display text-2xl break-words" data-testid="member-details-full-name">{member.full_name}</DialogTitle>
              <DialogDescription className="sr-only">Member directory and contact information.</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        {loading ? <p role="status" data-testid="member-details-loading" className="text-sm text-muted-foreground">Loading member details…</p> : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-3" data-testid="limited-member-details">
            <Field label="Name of Facility" value={member.facility_name} testId="member-details-facility" />
            <Field label="Professional Grade" value={member.professional_grade} testId="member-details-grade" />
            <div data-testid="member-details-status"><p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1">Status</p>
              <span className={`inline-block text-xs font-medium px-2.5 py-1 rounded-full ${status.cls}`}>{status.label}</span>
            </div>
            <Field label="Membership number" value={member.membership_id || "Not assigned"} testId="member-details-membership-number" />
            <Field label="Phone number" value={member.contact_number} testId="member-details-phone" />
            <Field label="Email address" value={member.email} testId="member-details-email" />
          </div>
        )}
        {!loading && ["approved", "pending_facility"].includes(member.application_status) && footerSlot && <div className="flex flex-wrap gap-2 pt-4 border-t border-border">{footerSlot}</div>}
      </DialogContent>
    </Dialog>
  );
}

export function MemberDetailsDialog({ member, open, onOpenChange, statusSlot, employment, extraSlot, footerSlot, loading }) {
  const { user } = useAuth();
  if (!member) return null;
  if (isLimitedMemberView(user.role)) return <LimitedMemberDetails member={member} open={open} onOpenChange={onOpenChange} loading={loading} footerSlot={user.role === "facility_manager" ? footerSlot : null} />;
  const edu = member.education || [];
  const emp = employment || {};
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="member-details-dialog">
        <DialogHeader>
          <div className="flex items-center gap-4">
            <Avatar member={member} size={64} />
            <div>
              <DialogTitle className="font-display text-2xl">
                {[member.title, member.first_name, member.last_name].filter(Boolean).join(" ")}
              </DialogTitle>
              <p className="text-xs text-muted-foreground font-mono mt-1">
                {member.staff_id}{member.membership_id ? ` · ${member.membership_id}` : ""}
              </p>
              <DialogDescription className="sr-only">Full member profile details</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        {statusSlot}

        {isNationalLevel(user.role) && <NationalExtras member={member} />}

        <Section title="Biodata">
          <Field label="Sex" value={member.sex} />
          <Field label="Date of Birth" value={member.date_of_birth} />
          <Field label="Age" value={member.age != null ? String(member.age) : ""} />
          <Field label="SSNIT Number" value={member.ssnit_number} />
        </Section>

        <Section title="Contact">
          <Field label="Contact Number" value={member.contact_number} />
          <Field label="Alternate Contact" value={member.alternate_contact_number} />
          <Field label="Email" value={member.email} />
        </Section>

        <Section title="Next of Kin">
          <Field label="Name" value={member.next_of_kin_name} />
          <Field label="Contact" value={member.next_of_kin_contact} />
          <Field label="Relationship" value={member.next_of_kin_relationship} />
        </Section>

        <Section title="Employment">
          <Field label="Facility" value={emp.facility ?? member.facility_name} />
          <Field label="Facility Type" value={emp.facility_type ?? member.facility_type} />
          <Field label="District" value={emp.district ?? member.district} />
          <Field label="Region" value={emp.region ?? member.region_name} />
          <Field label="Professional Grade" value={member.professional_grade} />
          <Field label="Rank" value={member.rank} />
          {emp.chapter && <Field label="Chapter" value={emp.chapter} />}
        </Section>

        <Section title="Identification & Family">
          <Field label="Ghana Card" value={member.ghana_card_number} />
          <Field label="Marital Status" value={member.marital_status} />
          {member.marital_status === "Married" && <Field label="Spouse" value={member.spouse_name} />}
          <Field label="No. of Children" value={member.number_of_children} />
          <Field label="Children's Names" value={member.children?.length
            ? member.children.map((c) => `${c.full_name} (${c.sex || "—"}, ${c.date_of_birth || "—"})`).join("; ")
            : member.biological_children_names} testId="member-children" />
          <Field label="Mother" value={member.mother_name ? `${member.mother_name}${member.mother_status ? ` (${member.mother_status})` : ""}` : ""} />
          <Field label="Father" value={member.father_name ? `${member.father_name}${member.father_status ? ` (${member.father_status})` : ""}` : ""} />
        </Section>

        <Section title="Emergency Contact">
          <Field label="Name" value={member.emergency_contact_name} />
          <Field label="Phone" value={member.emergency_contact_phone} />
          <Field label="Email" value={member.emergency_contact_email} />
          <Field label="Address" value={member.emergency_contact_address} />
        </Section>

        {member.worked_at_previous_facility === "yes" && (
          <Section title="Previous Employment">
            <Field label="Facility" value={member.previous_facility_name} />
            <Field label="Location" value={member.previous_facility_location} />
            <Field label="Type" value={member.previous_facility_type} />
          </Section>
        )}

        {edu.length > 0 && (
          <div className="mt-4">
            <p className="text-xs uppercase tracking-wider text-muted-foreground mb-2">Tertiary Education</p>
            <div className="space-y-2">
              {edu.map((e, i) => (
                <div key={i} className="rounded-md border border-border p-3 text-sm">
                  <p className="font-medium">{e.qualification || "—"}</p>
                  <p className="text-muted-foreground">{e.institution}{e.location ? `, ${e.location}` : ""}</p>
                  <p className="text-xs text-muted-foreground">{e.entry_year} – {e.graduation_year}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {member.rejection_reason && (
          <p className="text-sm text-destructive mt-3">Rejection note: {member.rejection_reason}</p>
        )}

        {extraSlot}
        {footerSlot && <div className="flex flex-wrap gap-2 pt-4 border-t border-border mt-4">{footerSlot}</div>}
      </DialogContent>
    </Dialog>
  );
}
